import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, ApiError, type CoinsQuery } from './client';
import type { TxSide } from './tx-types';
import type { CandleField, CandleTf, CoinSort, CoinWindow, LeaderboardWindow } from './types';

export const qk = {
  coins: (p: CoinsQuery) => ['coins', p.sort, p.window ?? null, p.q ?? null, p.limit ?? null] as const,
  coinsInf: (sort: CoinSort, window: CoinWindow | undefined, q: string | undefined) => ['coins-inf', sort, window ?? null, q ?? null] as const,
  coin: (mint: string) => ['coin', mint] as const,
  trades: (mint: string) => ['trades', mint] as const,
  candles: (mint: string, tf: CandleTf, field: CandleField) => ['candles', mint, tf, field] as const,
  holders: (mint: string) => ['holders', mint] as const,
  search: (q: string) => ['search', q] as const,
  profile: (w: string) => ['profile', w] as const,
  leaderboard: (w: LeaderboardWindow) => ['leaderboard', w] as const,
  balance: (mint: string, wallet: string) => ['balance', mint, wallet] as const,
};

const is404 = (e: unknown) => e instanceof ApiError && e.status === 404;

export function useCoins(p: CoinsQuery, opts: { refetchMs?: number; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.coins(p),
    queryFn: () => api.coins(p),
    refetchInterval: opts.refetchMs ?? 20_000,
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });
}

export function useCoinsInfinite(sort: CoinSort, window: CoinWindow | undefined, q?: string, pageSize = 30) {
  return useInfiniteQuery({
    queryKey: qk.coinsInf(sort, window, q),
    queryFn: ({ pageParam }) => api.coins({ sort, ...(window ? { window } : {}), ...(q ? { q } : {}), limit: pageSize, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * A coin's detail. A 404 is final ("Coin not found") unless `indexing`: right after a launch the indexer needs a few
 * seconds, so the page polls until the coin appears.
 */
export function useCoin(mint: string | undefined, opts: { indexing?: boolean } = {}) {
  return useQuery({
    queryKey: qk.coin(mint ?? ''),
    queryFn: () => api.coin(mint!),
    enabled: !!mint,
    staleTime: 3_000,
    retry: (n, e) => !is404(e) && n < 2,
    refetchInterval: (q) => (opts.indexing && !q.state.data && is404(q.state.error) ? 2_500 : false),
  });
}

export function useTrades(mint: string | undefined, opts: { limit?: number; enabled?: boolean } = {}) {
  const limit = opts.limit ?? 50;
  return useQuery({ queryKey: qk.trades(mint ?? ''), queryFn: () => api.trades(mint!, { limit }), enabled: !!mint && (opts.enabled ?? true), staleTime: 10_000 });
}

export function useCandles(mint: string | undefined, tf: CandleTf, field: CandleField, enabled = true) {
  return useQuery({
    queryKey: qk.candles(mint ?? '', tf, field),
    queryFn: () => api.candles(mint!, { tf, field }),
    enabled: !!mint && enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useHolders(mint: string | undefined, enabled = true) {
  return useQuery({ queryKey: qk.holders(mint ?? ''), queryFn: () => api.holders(mint!, { limit: 50 }), enabled: !!mint && enabled, staleTime: 15_000, refetchInterval: 30_000 });
}

export function useSearch(q: string) {
  const t = q.trim();
  return useQuery({ queryKey: qk.search(t), queryFn: () => api.search(t), enabled: t.length >= 1, staleTime: 10_000, placeholderData: keepPreviousData });
}

export function useProfile(wallet: string | undefined) {
  return useQuery({ queryKey: qk.profile(wallet ?? ''), queryFn: () => api.profile(wallet!), enabled: !!wallet, staleTime: 10_000 });
}

export function useLeaderboard(w: LeaderboardWindow) {
  return useQuery({ queryKey: qk.leaderboard(w), queryFn: () => api.leaderboard(w), staleTime: 30_000, placeholderData: keepPreviousData });
}

export interface QuoteParams {
  mint: string;
  side: TxSide;
  /** Lamports to spend (buy, fees included) or base units to sell; nothing is asked for null or 0. */
  amount: bigint | null;
  slippageBps: number;
  /** Makes the rent lines exact and adds the wallet's balances. */
  wallet: string | null;
}

/**
 * GET /api/quote (120 / min per IP, shared by every tab behind the IP). Callers debounce the amount; a new key
 * cancels the request in flight; an idle panel requotes every 10 s while the tab is visible, slower after a 429.
 * No priorityMicroLamports: on mainnet the server estimates it, and the build that follows uses the same estimate.
 */
export function useQuote(p: QuoteParams, enabled = true) {
  const amount = p.amount !== null && p.amount > 0n ? p.amount.toString() : null;
  return useQuery({
    queryKey: ['quote', p.mint, p.side, amount, p.slippageBps, p.wallet] as const,
    enabled: enabled && amount !== null,
    queryFn: ({ signal }) => api.quote({ mint: p.mint, side: p.side, amount: amount!, slippageBps: p.slippageBps, ...(p.wallet ? { wallet: p.wallet } : {}) }, signal),
    staleTime: 5_000,
    gcTime: 60_000,
    placeholderData: keepPreviousData,
    retry: (n, e) => e instanceof ApiError && (e.code === 'busy' || (e.code === 'unsupported' && e.details?.reason === 'not_open')) && n < 3,
    retryDelay: (_n, e) => 1000 * Math.min(30, Math.max(1, (e instanceof ApiError ? e.retryAfterSec : undefined) ?? 2)),
    refetchInterval: (q) => {
      const e = q.state.error;
      if (e instanceof ApiError && (e.code === 'not_found' || (e.code === 'unavailable' && e.details?.reason === 'setup'))) return false;
      return Math.max(10_000, 1000 * ((e instanceof ApiError ? e.retryAfterSec : undefined) ?? 0));
    },
  });
}

/** The trade panel reads the wallet's balances at most this often while it is open (and right after a trade). */
export const BALANCE_POLL_MS = 15_000;

/**
 * GET /api/balance (the quote bucket: 120 / min per IP, shared by every tab behind the IP): the wallet's SOL and coin
 * balance for the trade panel's MAX and sell percentages. Read when the panel opens, then every 15 s while it stays
 * open and the tab is visible; the key holds no amount, so typing never reads it. The panel reads it again after a
 * confirmed trade. Not a Raydius coin (404) and setup mode stop the polling.
 */
export function useBalance(wallet: string | null, mint: string, enabled = true) {
  return useQuery({
    queryKey: qk.balance(mint, wallet ?? ''),
    enabled: enabled && !!wallet,
    queryFn: ({ signal }) => api.balance(wallet!, mint, signal),
    staleTime: BALANCE_POLL_MS,
    gcTime: 60_000,
    refetchInterval: (q) => {
      const e = q.state.error;
      if (e instanceof ApiError && (e.code === 'not_found' || (e.code === 'unavailable' && e.details?.reason === 'setup'))) return false;
      return Math.max(BALANCE_POLL_MS, 1000 * ((e instanceof ApiError ? e.retryAfterSec : undefined) ?? 0));
    },
  });
}
